import React from "react";
import { useSelector } from "react-redux";
import TextInput from "../../controls/TextInput";
import {
  OnestBoldDefault,
  OnestNormalDefault,
  OnestSemiBoldDefault,
} from "../../styled/TextComponents";
import {
  color_grey_light,
  color_grey_ultra_light,
  color_red_default,
  color_white,
} from "../../../constants/colors";
import { useWindowDimensions } from "../../../utils";
import { GAMES_CATALOG } from "../Games/gamesCatalog";
import UpdateCardView from "./UpdateCardView";
import {
  UPDATE_SECTIONS,
  getUpdateSection,
} from "./updatesConstants";
import {
  composeChangeText,
  createUpdateAddress,
  getUpdateResourceId,
  resolveUpdateSectionKey,
  splitTitleAndBody,
} from "./updateContent";
import { useI18n } from "../../../i18n/I18nProvider";

const updatesAdminText = "#333333";
const updatesAdminMuted = "#707070";

const UpdatesAdminForm = ({
  mode = "create",
  initialChange,
  onCancel,
  onSubmit,
  isWaiting,
}) => {
  const { t } = useI18n();
  const libraryFiles = useSelector((state) => state.libraryFiles);
  const { width: screenWidth } = useWindowDimensions();
  const stacked = screenWidth <= 900;

  const initialSplit = splitTitleAndBody(initialChange?.text);
  const initialSplitEn = splitTitleAndBody(initialChange?.text_en);
  const initialResourceId = getUpdateResourceId(initialChange);
  const initialLibraryId =
    initialChange?.type === "library" ? String(initialResourceId) : "";
  const initialGameId =
    initialChange?.type === "game" ? String(initialResourceId) : "";

  const [title, setTitle] = React.useState(initialSplit.title);
  const [body, setBody] = React.useState(initialSplit.body);
  const [titleEn, setTitleEn] = React.useState(initialSplitEn.title);
  const [bodyEn, setBodyEn] = React.useState(initialSplitEn.body);
  const [previewLocale, setPreviewLocale] = React.useState("ru");
  const [sectionKey, setSectionKey] = React.useState(
    resolveUpdateSectionKey(initialChange),
  );
  const [libraryFileId, setLibraryFileId] = React.useState(initialLibraryId);
  const [gameId, setGameId] = React.useState(initialGameId);

  React.useEffect(() => {
    const split = splitTitleAndBody(initialChange?.text);
    const splitEn = splitTitleAndBody(initialChange?.text_en);
    setTitle(split.title);
    setBody(split.body);
    setTitleEn(splitEn.title);
    setBodyEn(splitEn.body);
    setLibraryFileId(
      initialChange?.type === "library"
        ? String(getUpdateResourceId(initialChange))
        : "",
    );
    setGameId(
      initialChange?.type === "game"
        ? String(getUpdateResourceId(initialChange))
        : "",
    );
    setSectionKey(resolveUpdateSectionKey(initialChange));
  }, [initialChange]);

  const section = getUpdateSection(sectionKey);
  const iconKey = section.iconKey;
  const selectedType =
    sectionKey === "games" && gameId
      ? "game"
      : sectionKey === "library" && libraryFileId
        ? "library"
        : "custom";
  const selectedResourceId =
    sectionKey === "games"
      ? gameId || null
      : sectionKey === "library"
        ? libraryFileId || null
        : null;
  const previewChange = {
    id: initialChange?.id || "preview",
    type: selectedType,
    address: createUpdateAddress({
      icon: iconKey,
      resourceId: selectedResourceId,
      section: sectionKey,
    }),
    text: composeChangeText(title, body),
    text_en: composeChangeText(titleEn, bodyEn),
    time_create: initialChange?.time_create || new Date().toISOString(),
  };

  const handleSubmit = () => {
    const payload = {
      title: title.trim(),
      body: body.trim(),
      iconKey,
      libraryFileId: libraryFileId ? Number(libraryFileId) : null,
      gameId: gameId ? Number(gameId) : null,
      text: composeChangeText(title, body),
      text_en: composeChangeText(titleEn, bodyEn),
      type: selectedType,
      address: createUpdateAddress({
        icon: iconKey,
        resourceId: selectedResourceId,
        section: sectionKey,
      }),
    };
    onSubmit(payload);
  };

  return (
    <div
      style={{
        width: "100%",
        background: color_white,
        borderRadius: 20,
        border: `1px solid ${color_grey_light}`,
        padding: stacked ? 16 : 24,
        boxSizing: "border-box",
      }}
    >
      <OnestSemiBoldDefault style={{ fontSize: 20, marginBottom: 16 }}>
        {mode === "create" ? t("Новая публикация") : t("Редактирование")}
      </OnestSemiBoldDefault>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: stacked ? "1fr" : "1fr 1fr",
          gap: 24,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <OnestNormalDefault style={{ color: updatesAdminText, fontWeight: 600 }}>
            {t("Русский")}
          </OnestNormalDefault>
          <TextInput
            title={t("Заголовок")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputBackgroundColor={color_grey_ultra_light}
            width="calc(100% - 30px)"
          />
          <TextInput
            title={t("Текст новости")}
            multiline
            value={body}
            onChange={(e) => setBody(e.target.value)}
            inputBackgroundColor={color_grey_ultra_light}
            width="calc(100% - 30px)"
            height={120}
          />

          <div
            style={{
              marginTop: 4,
              paddingTop: 16,
              borderTop: `1px solid ${color_grey_light}`,
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <div>
              <OnestNormalDefault style={{ color: updatesAdminText, fontWeight: 600 }}>
                {t("English")}
              </OnestNormalDefault>
              <OnestNormalDefault
                style={{ marginTop: 4, fontSize: 13, color: updatesAdminMuted }}
              >
                {t("Если оставить пустым, на английском интерфейсе покажется русский текст.")}
              </OnestNormalDefault>
            </div>
            <TextInput
              title={t("Заголовок на английском")}
              value={titleEn}
              onChange={(e) => setTitleEn(e.target.value)}
              inputBackgroundColor={color_grey_ultra_light}
              width="calc(100% - 30px)"
            />
            <TextInput
              title={t("Текст новости на английском")}
              multiline
              value={bodyEn}
              onChange={(e) => setBodyEn(e.target.value)}
              inputBackgroundColor={color_grey_ultra_light}
              width="calc(100% - 30px)"
              height={120}
            />
          </div>

          <div>
            <OnestNormalDefault style={{ marginBottom: 8, color: updatesAdminText }}>
              {t("Раздел")}
            </OnestNormalDefault>
            <select
              value={sectionKey}
              onChange={(event) => {
                const nextSection = event.target.value;
                setSectionKey(nextSection);
                if (nextSection !== "games") setGameId("");
                if (nextSection !== "library") setLibraryFileId("");
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${color_grey_light}`,
                fontFamily: "Onest, sans-serif",
                fontSize: 14,
                background: color_grey_ultra_light,
              }}
            >
              {UPDATE_SECTIONS.map((item) => (
                <option key={item.key} value={item.key}>
                  {t(item.label)}
                </option>
              ))}
            </select>
            <OnestNormalDefault
              style={{ marginTop: 8, fontSize: 13, color: updatesAdminMuted }}
            >
              {t("Иконка назначается автоматически для выбранного раздела.")}
            </OnestNormalDefault>
          </div>

          {sectionKey === "games" && (
            <div>
              <OnestNormalDefault style={{ marginBottom: 8, color: updatesAdminText }}>
                {t("Игра и обложка")}
              </OnestNormalDefault>
              <select
                value={gameId}
                onChange={(event) => setGameId(event.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: `1px solid ${color_grey_light}`,
                  fontFamily: "Onest, sans-serif",
                  fontSize: 14,
                  background: color_grey_ultra_light,
                }}
              >
                <option value="">{t("Без конкретной игры")}</option>
                {GAMES_CATALOG.map((game) => (
                  <option key={game.id} value={game.id}>
                    {t(game.name)}
                  </option>
                ))}
              </select>
              <OnestNormalDefault
                style={{ marginTop: 8, fontSize: 13, color: updatesAdminMuted }}
              >
                {t("Обложка и ссылка подставятся из раздела «Игры». На телефоне обложка скрывается.")}
              </OnestNormalDefault>
            </div>
          )}

          {sectionKey === "library" && (
            <div>
              <OnestNormalDefault style={{ marginBottom: 8, color: updatesAdminText }}>
                {t("Ссылка на материал (необязательно)")}
              </OnestNormalDefault>
              <select
                value={libraryFileId}
                onChange={(event) => setLibraryFileId(event.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: `1px solid ${color_grey_light}`,
                  fontFamily: "Onest, sans-serif",
                  fontSize: 14,
                  background: color_grey_ultra_light,
                }}
              >
                <option value="">{t("Без ссылки")}</option>
                {libraryFiles.map((file) => (
                  <option key={file.id} value={file.id}>
                    {file.name || file.name_en || file.id}
                  </option>
                ))}
              </select>
              <OnestNormalDefault
                style={{ marginTop: 8, fontSize: 13, color: updatesAdminMuted }}
              >
                {t("В ленте будет тематическая иконка без изображения.")}
              </OnestNormalDefault>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={isWaiting}
                style={{
                  padding: "10px 18px",
                  borderRadius: 10,
                  border: `1px solid ${color_grey_light}`,
                  background: color_white,
                  fontFamily: "Onest, sans-serif",
                  cursor: "pointer",
                }}
              >
                {t("Отмена")}
              </button>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isWaiting || !title.trim()}
              style={{
                padding: "10px 18px",
                borderRadius: 10,
                border: "none",
                background: color_red_default,
                color: color_white,
                fontFamily: "Onest, sans-serif",
                fontWeight: 600,
                cursor: "pointer",
                opacity: isWaiting || !title.trim() ? 0.6 : 1,
              }}
            >
              {mode === "create" ? t("Опубликовать") : t("Сохранить")}
            </button>
          </div>
        </div>

        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 12,
            }}
          >
            <OnestBoldDefault style={{ fontSize: 16 }}>
              {t("Превью")}
            </OnestBoldDefault>
            <div
              role="group"
              aria-label={t("Язык превью")}
              style={{ display: "flex", gap: 6 }}
            >
              {["ru", "en"].map((language) => (
                <button
                  key={language}
                  type="button"
                  onClick={() => setPreviewLocale(language)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 8,
                    border: `1px solid ${
                      previewLocale === language ? color_red_default : color_grey_light
                    }`,
                    background:
                      previewLocale === language ? color_white : color_grey_ultra_light,
                    color: updatesAdminText,
                    fontFamily: "Onest, sans-serif",
                    fontSize: 12,
                    fontWeight: previewLocale === language ? 600 : 400,
                    cursor: "pointer",
                  }}
                >
                  {language.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <UpdateCardView
            change={previewChange}
            featured
            previewLocale={previewLocale}
          />
        </div>
      </div>
    </div>
  );
};

export default UpdatesAdminForm;
